import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import analyticsService from "../../../services/analyticsService";

// Register Chart.js components + DataLabels plugin
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

export default function LeadAnalytics({ selectedTab, filters }) {
  const [hiddenDatasets, setHiddenDatasets] = useState({});
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(scrollContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const isMonthView = selectedTab === "This Month" || selectedTab === "Previous Month";
  const isLifetime = selectedTab === "Life Time";

  // Load chart data when selectedTab changes (matches React Native app)
  useEffect(() => {
    const loadChartData = async () => {
      try {
        setLoading(true);
        setError(null);

        // When custom dates are set, clear timeRange (exact app logic)
        const activeTimeRange = (filters?.startDate && filters?.endDate) ? null : selectedTab;
        const response = await analyticsService.getUserAnalyticsChart(activeTimeRange, {
          startDate: filters?.startDate || null,
          endDate: filters?.endDate || null,
        });

        if (response.success) {
          // apiUtils wraps response: response.data may be array directly or { data: [...] }
          const dataArr = Array.isArray(response.data) ? response.data
            : Array.isArray(response.data?.data) ? response.data.data : [];
          setChartData(dataArr);
        } else {
          setChartData([]);
        }
      } catch (err) {
        console.error('Error loading chart data:', err);
        setError(err.message || 'Failed to load chart data');
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    loadChartData();
  }, [selectedTab, filters?.startDate, filters?.endDate]);

  // Reset scroll to start so first 12 months are visible
  useEffect(() => {
    if (!loading && scrollContainerRef.current && chartData.length > 12) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [loading, chartData.length]);

  // Map API chart data to user analytics format (matches React Native app AdminHomeS.js exactly)
  const rawData = chartData.map(item => ({
    a: item.total_users || 0,
    b: item.total_agents || 0,
    c: item.total_agencies || 0,
    d: item.total_builders || 0,
    e: item.total_projects || 0,
    f: item.subscribed_agents || 0,
    g: item.subscribed_agencies || 0,
    h: item.subscribed_builders || 0,
    i: item.unsubscribed_agents || 0,
    j: item.unsubscribed_agencies || 0,
    k: item.unsubscribed_builders || 0,
  }));

  const keys = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k"];
  const colors = ["#5C9AFF", "#66BB6A", "#26A69A", "#F06292", "#FFB74D", "#388E3C", "#00897B", "#AB47BC", "#EF5350", "#FF7043", "#42A5F5"];
  // Generate appropriate labels based on view type and data (EXACT React Native app logic)
  const chartLabels = chartData.map((item, index) => {
    if (isMonthView) {
      // For month views, show "Week X" format like mobile app
      return item.week || item.month || `Week ${index + 1}`;
    } else {
      // For year view, show month names
      return item.month || '';
    }
  });

  // Segment name mapping (matches React Native app AdminHomeS.js exactly)
  const segmentNames = {
    a: 'Total Users',
    b: 'Total Agents',
    c: 'Total Agencies',
    d: 'Total Builders',
    e: 'Total Projects',
    f: 'Subscribed Agents',
    g: 'Subscribed Agencies',
    h: 'Subscribed Builders',
    i: 'Unsubscribed Agents',
    j: 'Unsubscribed Agencies',
    k: 'Unsubscribed Builders',
  };

  // Build chart data from API response (EXACT React Native app logic)
  const data = {
    labels: chartLabels.length > 0 ? chartLabels : (isMonthView ? ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"] : ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]),
    datasets: keys.map((key, i) => ({
      label: segmentNames[key],
      data: rawData.length > 0 ? rawData.map(item => item[key]) : (isMonthView ? [0, 0, 0, 0, 0] : isLifetime ? [] : Array(12).fill(0)),
      backgroundColor: colors[i],
      hidden: hiddenDatasets[i] || false,
      barPercentage: isMonthView ? 0.3 : isLifetime ? 0.3 : 0.9,
      categoryPercentage: isMonthView ? 0.3 : isLifetime ? 0.3 : 0.9,
    })),
  };

  // Custom plugin to show totals on top of bars
  const totalLabelsPlugin = {
    id: 'totalLabels',
    afterDatasetsDraw: (chart) => {
      const { ctx, data, scales } = chart;
      ctx.save();
      ctx.font = 'bold 12px Arial';
      ctx.fillStyle = '#333';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      data.labels.forEach((_, index) => {
        // Only sum visible (non-hidden) datasets
        const total = data.datasets.reduce((sum, dataset, datasetIndex) => {
          if (chart.getDatasetMeta(datasetIndex).hidden) return sum;
          return sum + (dataset.data[index] || 0);
        }, 0);

        if (total <= 0) return;

        const x = scales.x.getPixelForValue(index);
        const y = scales.y.getPixelForValue(total) - 4;

        ctx.fillText(total.toString(), x, y);
      });

      ctx.restore();
    }
  };

  // For many data points, each label gets 80px; minimum 100% of container
  const needsScroll = chartData.length > 12;
  // Each bar gets containerWidth/12 px so exactly 12 bars fill the view; extras scroll
  const perBar = containerWidth > 0 ? containerWidth / 12 : 80;
  const chartWidth = needsScroll ? chartData.length * perBar : undefined;

  const options = {
    responsive: true,
    maintainAspectRatio: !needsScroll,
    layout: {
      padding: { top: 24 },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#fff",
        font: { weight: "bold", size: 12 },
        anchor: "center",
        align: "center",
        formatter: (value) => value > 0 ? value : '', // Hide zeros
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { color: "#000", font: { weight: "bold", size: 12 } },
        grid: {
          display: false, // Remove vertical grid lines
        },
      },
      y: {
        stacked: true,
        ticks: { color: "#666", font: { size: 11 } },
        grid: {
          display: false, // Remove horizontal grid lines
        },
      },
    },
  };

  const toggleDataset = (i) => {
    setHiddenDatasets((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  // Check if all datasets are visible
  const allVisible = keys.every((_, i) => !hiddenDatasets[i]);

  // Toggle all datasets
  const toggleAllDatasets = () => {
    if (allVisible) {
      // Hide all
      const newHidden = {};
      keys.forEach((_, i) => {
        newHidden[i] = true;
      });
      setHiddenDatasets(newHidden);
    } else {
      // Show all
      setHiddenDatasets({});
    }
  };

  return (
    <div className="card mb-4 analytics-card" style={{ overflow: "hidden" }}>
      <div className="card-body">
        <h5 className="fw-bold">User Analytics</h5>
        <p className="text-muted small">
          {selectedTab === 'This Year'
            ? `Jan - Dec ${new Date().getFullYear()}`
            : selectedTab === 'This Month'
              ? new Date().toLocaleString('default', { month: 'long', year: 'numeric' })
              : new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleString('default', { month: 'long', year: 'numeric' })}
        </p>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            <h6 className="mb-1">Failed to Load Chart Data</h6>
            <p className="mb-0">{error}</p>
          </div>
        ) : rawData.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
            <p className="text-muted">No data available</p>
          </div>
        ) : (
          /* Chart */
          <div
            ref={scrollContainerRef}
            style={{
              overflowX: needsScroll ? "auto" : "visible",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              maxWidth: "100%",
              width: "100%",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <div style={{ width: chartWidth, height: needsScroll ? 380 : undefined, minWidth: "100%" }}>
              <Bar data={data} options={options} plugins={[ChartDataLabels, totalLabelsPlugin]} />
            </div>
          </div>
        )}

        {/* Toggles */}
        <div
          className="leads-centers"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            minHeight: "100%",
          }}
        >
          <div
            className="chart-toggles"
            style={{
              width: "100%",
              maxWidth: "900px",
              marginTop: "20px",
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "12px 16px",
              alignItems: "center",
            }}
          >
            {/* Select All Resources Checkbox */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              <span
                onClick={toggleAllDatasets}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "4px",
                  backgroundColor: allVisible ? "#6366f1" : "#e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "8px",
                  transition: "all 0.2s ease",
                }}
              >
                {allVisible && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3"
                    viewBox="0 0 24 24"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              Select All Resources
            </label>

            {keys.map((key, i) => {
              const color = colors[i];
              const label = segmentNames[key];
              const isChecked = !hiddenDatasets[i];
              return (
                <label
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  <span
                    onClick={() => toggleDataset(i)}
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "4px",
                      backgroundColor: isChecked ? color : "#e5e7eb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "8px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isChecked && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {label}
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}